import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { UserPlus, Trash2, Pencil, Shield, KeyRound } from "lucide-react";

const ROLE_STYLES = {
  owner: "bg-[#0A1C40] text-[#FFC220]",
  manager: "bg-[#FFC220] text-[#0A1C40]",
  associate: "bg-[#15803d] text-white",
  driver: "bg-blue-700 text-white",
};

const EMPTY = { email: "", name: "", role: "associate", password: "", manager_pin: "", phone: "" };

export default function AdminStaff() {
  const [items, setItems] = useState([]);
  const [assignableRoles, setAssignableRoles] = useState(["owner", "manager", "associate", "driver"]);
  const [limits, setLimits] = useState({});
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/admin/staff").then((r) => {
    setItems(r.data.items);
    setAssignableRoles(r.data.assignable_roles || assignableRoles);
    setLimits(r.data.discount_limits || {});
    setLoading(false);
  }).catch((err) => {
    toast.error(err?.response?.data?.detail || "Failed to load staff");
    setLoading(false);
  });

  useEffect(() => { load(); }, []); // eslint-disable-line

  const startCreate = () => { setEditing("new"); setForm(EMPTY); };
  const startEdit = (u) => {
    setEditing(u.id);
    setForm({ email: u.email, name: u.name || "", role: u.role, password: "", manager_pin: "" });
  };
  const cancelEdit = () => { setEditing(null); setForm(EMPTY); };

  const save = async () => {
    try {
      if (editing === "new") {
        const body = { ...form };
        if (!body.manager_pin) delete body.manager_pin;
        if (!body.phone) delete body.phone;
        const { data } = await api.post("/admin/staff", body);
        toast.success(
          `Created ${body.email} — invite sent by email${data.invite_sms_queued ? " + text" : ""}`
        );
      } else {
        const body = {};
        if (form.name) body.name = form.name;
        if (form.role) body.role = form.role;
        if (form.password) body.password = form.password;
        if (form.manager_pin) body.manager_pin = form.manager_pin;
        await api.patch(`/admin/staff/${editing}`, body);
        toast.success("Staff updated");
      }
      cancelEdit();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const deactivate = async (u) => {
    if (!window.confirm(`Deactivate ${u.email}? They will no longer be able to log in.`)) return;
    try {
      await api.delete(`/admin/staff/${u.id}`);
      toast.success(`Deactivated ${u.email}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Deactivate failed");
    }
  };

  const activate = async (u) => {
    try {
      await api.patch(`/admin/staff/${u.id}`, { is_active: true });
      toast.success(`Reactivated ${u.email}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Activate failed");
    }
  };

  return (
    <div className="p-6" data-testid="admin-staff-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="ipv-eyebrow text-[#FFC220]">Owner-only</div>
          <h1 className="font-display font-black text-3xl tracking-tight text-[#0A1C40]">Staff Accounts</h1>
        </div>
        <button onClick={startCreate} className="btn-gold inline-flex items-center gap-2" data-testid="staff-new-btn">
          <UserPlus className="w-4 h-4" /> New staff member
        </button>
      </div>

      {/* Role & discount reference */}
      <div className="mb-6 grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="role-legend">
        {assignableRoles.map((r) => (
          <div key={r} className="border border-gray-200 p-3">
            <div className={`inline-block px-2 py-0.5 text-[10px] font-black uppercase tracking-widest ${ROLE_STYLES[r] || "bg-gray-200 text-gray-700"}`}>{r}</div>
            {limits[r] && (
              <div className="text-[11px] text-gray-600 mt-2">
                Discount cap: single {limits[r].single_max_pct}% · bundle {limits[r].bundle_max_pct}%
              </div>
            )}
          </div>
        ))}
      </div>

      {loading && <div className="text-sm text-gray-500">Loading…</div>}

      {editing && (
        <div className="mb-6 border-2 border-[#0A1C40] p-5 bg-[#EEF3FF]" data-testid="staff-form">
          <div className="ipv-eyebrow mb-4">{editing === "new" ? "New staff" : "Edit staff"}</div>
          <div className="grid grid-cols-2 gap-4">
            {editing === "new" && (
              <div>
                <label className="ipv-eyebrow block mb-1">Email</label>
                <input className="input-base" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="staff-form-email" />
              </div>
            )}
            {editing === "new" && (
              <div>
                <label className="ipv-eyebrow block mb-1">Phone (optional — texts them the invite)</label>
                <input className="input-base" placeholder="(616) 555-0123" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="staff-form-phone" />
              </div>
            )}
            <div>
              <label className="ipv-eyebrow block mb-1">Name</label>
              <input className="input-base" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="staff-form-name" />
            </div>
            <div>
              <label className="ipv-eyebrow block mb-1">Role</label>
              <select className="input-base" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="staff-form-role">
                {assignableRoles.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div>
              <label className="ipv-eyebrow block mb-1">
                {editing === "new" ? "Password" : "New password (leave blank to keep)"}
              </label>
              <input type="password" className="input-base" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="staff-form-password" />
            </div>
            {form.role === "manager" && (
              <div>
                <label className="ipv-eyebrow block mb-1 flex items-center gap-1">
                  <KeyRound className="w-3 h-3" /> Manager PIN (4 digits)
                </label>
                <input
                  className="input-base tracking-[.5em]"
                  maxLength={4}
                  inputMode="numeric"
                  pattern="[0-9]{4}"
                  value={form.manager_pin}
                  onChange={(e) => setForm({ ...form, manager_pin: e.target.value.replace(/\D/g, "").slice(0, 4) })}
                  placeholder="••••"
                  data-testid="staff-form-pin"
                />
                <div className="text-[10px] text-gray-500 mt-1">
                  Used only to authorise discounts on an associate's order. Not a login credential.
                </div>
              </div>
            )}
          </div>
          <div className="mt-4 flex gap-2">
            <button onClick={save} className="btn-navy" data-testid="staff-form-save">Save</button>
            <button onClick={cancelEdit} className="text-sm text-gray-500 underline">Cancel</button>
          </div>
        </div>
      )}

      <div className="border border-gray-200 overflow-hidden">
        <table className="w-full text-sm" data-testid="staff-table">
          <thead className="bg-gray-50 text-left">
            <tr>
              <th className="p-3 ipv-eyebrow">Role</th>
              <th className="p-3 ipv-eyebrow">Email</th>
              <th className="p-3 ipv-eyebrow">Name</th>
              <th className="p-3 ipv-eyebrow">PIN</th>
              <th className="p-3 ipv-eyebrow">Active</th>
              <th className="p-3 ipv-eyebrow text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {items.map((u) => (
              <tr key={u.id} className={u.is_active === false ? "opacity-40 bg-gray-50" : ""} data-testid={`staff-row-${u.email}`}>
                <td className="p-3">
                  <span className={`inline-block px-2 py-0.5 text-[10px] font-black uppercase tracking-widest ${ROLE_STYLES[u.role] || "bg-gray-200 text-gray-700"}`}>{u.role}</span>
                </td>
                <td className="p-3 font-mono text-xs">{u.email}</td>
                <td className="p-3">{u.name || <span className="text-gray-400 italic">—</span>}</td>
                <td className="p-3">{u.has_manager_pin ? <Shield className="w-4 h-4 text-[#15803d]" /> : <span className="text-gray-300">—</span>}</td>
                <td className="p-3">{u.is_active === false ? <span className="text-red-600 text-xs font-bold">DEACTIVATED</span> : <span className="text-green-700 text-xs font-bold">ACTIVE</span>}</td>
                <td className="p-3 text-right">
                  <button onClick={() => startEdit(u)} className="text-[#0A1C40] hover:bg-gray-100 p-1.5" title="Edit" data-testid={`staff-edit-${u.email}`}>
                    <Pencil className="w-4 h-4" />
                  </button>
                  {u.is_active === false ? (
                    <button onClick={() => activate(u)} className="text-green-700 hover:bg-green-50 p-1.5 ml-1 text-xs font-bold" data-testid={`staff-activate-${u.email}`}>
                      REACTIVATE
                    </button>
                  ) : (
                    <button onClick={() => deactivate(u)} className="text-red-700 hover:bg-red-50 p-1.5 ml-1" title="Deactivate" data-testid={`staff-deactivate-${u.email}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {!items.length && !loading && (
              <tr><td colSpan={6} className="p-6 text-center text-sm text-gray-500">No staff yet. Click "New staff member" to add one.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
